/**
 * Conversion API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound ConversionApi.
 * Each tool maps to a method on the C# ConversionApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface ConversionApiToolSpec {
  /** Method name on the JS-side conversionApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: ConversionApiToolSpec[] = [
  // ── Discovery ─────────────────────────────────────────────────────────
  {
    method: "getConversionFileDetail",
    name: "WS-conversionApi-getConversionFileDetail",
    description:
      "Returns the full detail of a conversion file including all feature-to-feature " +
      "and attribute-to-attribute mappings. Provide the full file path from getConversionFiles.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
      },
      required: ["filePath"],
    },
    argOrder: ["filePath"],
  },
  {
    method: "getProjectRuleFolders",
    name: "WS-conversionApi-getProjectRuleFolders",
    description:
      "Returns a JSON array of project rule folder paths for conversion rules.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── CRUD ──────────────────────────────────────────────────────────────
  {
    method: "createConversionFile",
    name: "WS-conversionApi-createConversionFile",
    description:
      "Creates a new empty conversion file (.cf.xml). " +
      "jsonRequest is a JSON string with fields: name (required), description, " +
      "sourceFormat, sourceVersion, destinationFormat, destinationVersion, " +
      "sourceFeatureCatalog, destinationFeatureCatalog. " +
      "Returns { success, fullPath } on success.",
    parameters: {
      type: "object",
      properties: {
        jsonRequest: {
          type: "string",
          description:
            "JSON-serialized CreateConversionFileRequest. " +
            "Required field: name. Optional: description, sourceFormat, sourceVersion, " +
            "destinationFormat, destinationVersion, sourceFeatureCatalog, destinationFeatureCatalog.",
        },
      },
      required: ["jsonRequest"],
    },
    argOrder: ["jsonRequest"],
  },
  {
    method: "updateConversionFileMetadata",
    name: "WS-conversionApi-updateConversionFileMetadata",
    description:
      "Updates the metadata (name and/or description) of an existing conversion file.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
        name: {
          type: "string",
          description:
            "New name for the conversion file. Pass empty to keep current.",
        },
        description: {
          type: "string",
          description: "New description. Pass empty to keep current.",
        },
      },
      required: ["filePath", "name", "description"],
    },
    argOrder: ["filePath", "name", "description"],
  },
  {
    method: "addFeatureMapping",
    name: "WS-conversionApi-addFeatureMapping",
    description:
      "Adds a feature-to-feature mapping to a conversion file. " +
      "Maps a source feature type ID to a destination feature type ID. " +
      "Use getTypeCatalog tools to look up feature type IDs. " +
      "Returns { success, index } where index is the position of the new mapping.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
        sourceFeatureTypeId: {
          type: "number",
          description: "Source feature type ID.",
        },
        destFeatureTypeId: {
          type: "number",
          description: "Destination feature type ID.",
        },
        active: {
          type: "boolean",
          description: "Whether this mapping is active. Defaults to true.",
        },
      },
      required: ["filePath", "sourceFeatureTypeId", "destFeatureTypeId"],
    },
    argOrder: [
      "filePath",
      "sourceFeatureTypeId",
      "destFeatureTypeId",
      "active",
    ],
  },
  {
    method: "removeFeatureMapping",
    name: "WS-conversionApi-removeFeatureMapping",
    description:
      "Removes a feature-to-feature mapping by its index. " +
      "Get the index from getConversionFileDetail.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
        index: {
          type: "number",
          description: "Zero-based index of the feature mapping to remove.",
        },
      },
      required: ["filePath", "index"],
    },
    argOrder: ["filePath", "index"],
  },
  {
    method: "addAttributeMapping",
    name: "WS-conversionApi-addAttributeMapping",
    description:
      "Adds an attribute-to-attribute mapping to a conversion file. " +
      "filePath is the conversion file path. jsonRequest is a JSON string with fields: " +
      "sourceFeatureTypeId, sourceAttributeTypeId, destFeatureTypeId, destAttributeTypeId, " +
      "defaultValue (optional), overrideExistingValue (bool). " +
      "Use getTypeCatalog tools to look up feature and attribute type IDs. " +
      "Returns { success, index }.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
        jsonRequest: {
          type: "string",
          description:
            "JSON-serialized attribute mapping request. Fields: " +
            "sourceFeatureTypeId (int), sourceAttributeTypeId (int), " +
            "destFeatureTypeId (int), destAttributeTypeId (int), " +
            "defaultValue (string, optional), overrideExistingValue (bool).",
        },
      },
      required: ["filePath", "jsonRequest"],
    },
    argOrder: ["filePath", "jsonRequest"],
  },
  {
    method: "removeAttributeMapping",
    name: "WS-conversionApi-removeAttributeMapping",
    description:
      "Removes an attribute-to-attribute mapping by its index. " +
      "Get the index from getConversionFileDetail.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file.",
        },
        index: {
          type: "number",
          description: "Zero-based index of the attribute mapping to remove.",
        },
      },
      required: ["filePath", "index"],
    },
    argOrder: ["filePath", "index"],
  },
  {
    method: "deleteConversionFile",
    name: "WS-conversionApi-deleteConversionFile",
    description:
      "Deletes a conversion file. Only user-writable files can be deleted (not read-only). " +
      "Returns { success } on success.",
    parameters: {
      type: "object",
      properties: {
        filePath: {
          type: "string",
          description: "Full path to the conversion file to delete.",
        },
      },
      required: ["filePath"],
    },
    argOrder: ["filePath"],
  },

  // ── Execution ─────────────────────────────────────────────────────────
  {
    method: "runConversion",
    name: "WS-conversionApi-runConversion",
    description:
      "Runs a conversion using the specified conversion file on the active model. " +
      "optionsJson is an optional JSON string with: useSelection (bool, default false), " +
      "convertTasks (bool, default true), convertNetElements (bool, default true). " +
      "Returns { success, featuresCreated, tasksConverted, netElementsConverted, errors }.",
    parameters: {
      type: "object",
      properties: {
        conversionFilePath: {
          type: "string",
          description: "Full path to the conversion file to run.",
        },
        optionsJson: {
          type: "string",
          description:
            "Optional JSON string with run options: " +
            "useSelection (bool), convertTasks (bool), convertNetElements (bool). " +
            "Defaults: { useSelection: false, convertTasks: true, convertNetElements: true }.",
        },
      },
      required: ["conversionFilePath"],
    },
    argOrder: ["conversionFilePath", "optionsJson"],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const conversionApi = api?.conversionApi;
      if (!conversionApi) {
        return JSON.stringify({
          success: false,
          error:
            "ConversionApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = conversionApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: conversionApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "conversionApi",
        conversionApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every ConversionApi method.
 * Call this after `CefSharp.BindObjectAsync("conversionApi")` has resolved.
 */
export function createConversionApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
